/* eslint-disable react/display-name */
import React, { FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { Alert, Button, Card, Col, Row, Form } from 'react-bootstrap'
import BootstrapTable from 'react-bootstrap-table-next'
import ToolkitProvider from 'react-bootstrap-table2-toolkit'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Sidebar from '../Sidebar'
import Status from '../Status'
import LocationApplicationItem from '../../../types/LocationApplicationItem'
import LocationApplicationType from '../../../types/LocationApplicationType'
import { startCase } from 'lodash'
import { getItemModal, setItemModal } from '../../../utils/storage'
import './index.css'

interface Props {
  locationApplication: LocationApplicationType;
  onOpenModal: (modalToOpen: string, modalHeader: string, readonly: boolean) => void;
  onOpenRejectionNote: (rejectionNote: LocationApplicationItem) => void;
  readOnly: boolean;
}

const LocationApplication: FC<Props> = ({ locationApplication, onOpenModal, onOpenRejectionNote, readOnly }: Props) => {
  const localItemModal = getItemModal()
  const [showAlert, setShowAlert] = useState(!localItemModal)
  const [welcomeLanguage, setWelcomeLanguage] = useState('English')

  const { applicationWelcomeAlert } = useSelector((state: any) => ({
    applicationWelcomeAlert: state.applicationWelcomeAlert.data
  }))

  const handleCloseModal = () => {
    setShowAlert(false)
    setItemModal()
  }

  const inProgressColumns = [{
    dataField: 'status',
    text: 'Status',
    sort: false,
    formatter: (cellContent, row) => cellContent === 'SUBMITTED' ? <FontAwesomeIcon icon={['fas', 'check-circle']} size='2x' color='#75b465' /> : <FontAwesomeIcon icon={['fas', 'circle']} size='2x' color='#cccccc' />,
    headerStyle: (column, colIndex) => {
      return { width: '10%' }
    }
  }, {
    dataField: 'itemType',
    text: 'Application Item',
    sort: false,
    formatter: (cellContent) => cellContent === 'Copy of Facility Licensure' && locationApplication.applicationType === 'non-residential' ? 'Copy of Agency Licensure' : cellContent,
    headerStyle: (column, colIndex) => {
      return { width: '75%' }
    }
  }, {
    dataField: 'actions',
    text: 'Actions',
    sort: false,
    formatter: (cellContent, row) => {
      if (readOnly) {
        return (
          <Button className="action-button" variant="secondary" disabled>
            View
          </Button>
        );
      }

      return row.status === 'SUBMITTED' ? (
        <Button
          className='action-button'
          variant='outline-secondary'
          onClick={() => onOpenModal(row.modalType, row.itemType, false)}
        >
          View/Edit
        </Button>
      ) : (
        <Button
          className='action-button'
          variant='info'
          onClick={() => onOpenModal(row.modalType, row.itemType, false)}
        >
          Start
        </Button>
      );
    }
  }];

  const columns = [{
    dataField: 'itemType',
    text: 'Application Item',
    sort: false,
    formatter: (cellContent) => cellContent === 'Copy of Facility Licensure' && locationApplication.applicationType === 'non-residential' ? 'Copy of Agency Licensure' : cellContent,
    headerStyle: (column, colIndex) => {
      return { width: '45%' }
    }
  }, {
    dataField: 'status',
    text: 'Approval Status',
    sort: false,
    formatter: (cellContent, row) => <Status cellContent={cellContent} row={row} />
  }, {
    dataField: 'actions',
    text: 'Actions',
    sort: false,
    formatter: (cellContent, row) => {
      if (readOnly) {
        return (
          <Button className="action-button" variant="secondary" disabled>
            View
          </Button>
        );
      }

      return row.status === 'REJECTED' ? (
        <>
          <Button
            className='action-button'
            variant='outline-secondary'
            onClick={() => onOpenModal(row.modalType, row.itemType, false)}
          >
            View/Edit
          </Button>
          <Button
            className='action-button'
            variant='outline-secondary'
            onClick={() => onOpenRejectionNote(row)}
          >
            Note
          </Button>
        </>
      ) : (
        <Button
          className='action-button'
          variant='outline-secondary'
          onClick={() => onOpenModal(row.modalType, row.itemType, true)}
        >
          View
        </Button>
      );
    },
    headerStyle: (column, colIndex) => {
      return { width: '20%' }
    }
  }];

  const welcomeMessage = applicationWelcomeAlert.filter((alert) => alert.type === 'application').find(alert => alert.language === welcomeLanguage)

  return (
    <div>
      <Row>
        <Col xs={3}>
          <Sidebar
            label='Application'
            type={(locationApplication && startCase(locationApplication.applicationType)) || ''}
            startDate={(locationApplication && locationApplication.startDate) || null}
            submitDate={(locationApplication && locationApplication.submitDate) || null}
            approvalDate={(locationApplication && locationApplication.approvalDate) || null}
          />
        </Col>
        <Col xs={9}>
          {
            showAlert ? (
              <Alert variant='info' dismissible onClose={handleCloseModal}>
                <div className='applicant-welcome-alert-title'>
                  <h5>Welcome to the Lakeland Care Inc. Provider Application Process</h5>
                  <Form.Control as="select" className='applicant-welcome-language-select' value={welcomeLanguage} onChange={(e: any) => setWelcomeLanguage(e.target.value)}>
                    {applicationWelcomeAlert.filter((alert) => alert.type === 'application').map(alert => <option key={alert.language}>{alert.language}</option>)}
                  </Form.Control>
                </div>
                <div dangerouslySetInnerHTML={{ __html: (welcomeMessage && welcomeMessage.description) || '' }} ></div>
              </Alert>
            ) : null
          }
          <div id='container'>
            <Card id='card1'>
              <Card.Body className='card1Body'>
                <Card.Title>Application Items</Card.Title>
                <div>
                  <ToolkitProvider
                    keyField='locationApplicationItemId'
                    bootstrap4
                    data={(locationApplication && locationApplication.item) || []}
                    columns={(locationApplication && locationApplication.status === 'STARTED') ? inProgressColumns : columns}
                    search
                  >
                    {
                      (props: any) => (
                        <div className='application-table-container' style={{ width: '100%', background: 'white' }}>
                          <BootstrapTable
                            id='application_table'
                            bordered={false}
                            condensed
                            bootstrap4
                            noDataIndication={() => <div>No items found</div>}
                            {...props.baseProps}
                          />
                        </div>
                      )
                    }
                  </ToolkitProvider>
                </div>
              </Card.Body>
            </Card>
            {
              locationApplication && locationApplication.applicationType === 'residential' && locationApplication['12BedCertification'] && locationApplication.certificationStatus !== 'APPROVED' ? (
                <Card id='card2'>
                  <Card.Body className='card2Body'>
                    <Card style={{ width: '40%' }}>
                      <Card.Body className='card2BodyContainer'>
                        <FontAwesomeIcon icon={['fas', 'info-circle']} size='3x' color='#597eaa' />
                        <div style={{ marginTop: 10, textAlign: 'center' }}>
                          You must first complete the Certification Process before accessing the application items.
                        </div>
                      </Card.Body>
                    </Card>
                  </Card.Body>
                </Card>
              ) : null
            }
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default LocationApplication